import { defineStore } from 'pinia';
import { EPlayerWritableGroup, type IVipData, type IVipStrapiResponse } from '~/types/vip';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';

/*
  MAX_USER_GROUP_REQUESTS - how many times will action try to fetch new groups
  USER_GROUP_REQUEST_DELAY - delay between each request mentioned above
*/
const MAX_USER_GROUP_REQUESTS = 10;
const USER_GROUP_REQUEST_DELAY = 60 * 1000;

export const useStoreVipLevelData = defineStore('vipLevelData', {
  state: () => ({
    data: [] as IVipData[],
    isNotificationShown: false,
    isPolingGroups: false,
    requestPromise: null as Promise<IVipStrapiResponse> | null,
  }),

  getters: {
    currentLevelData(): IVipData | null {
      return this.data.find((data) => data.level === useStoreSsPlayer().playerLevel) || null;
    },
    compPointsNextLevel(): number | null {
      const nextLevel = useStoreSsPlayer().playerLevel + 1;
      const nextLevelData = useStoreVipLevelData().data.find((lvl) => lvl.level === nextLevel);
      const compoints = Number(nextLevelData?.compoints);

      return compoints >= 0 ? compoints : null;
    },
    maximumLevel(): number {
      let maxLevel = 0;

      this.data.forEach((el) => (maxLevel = el.level > maxLevel ? el.level : maxLevel));

      return maxLevel;
    },
    isVipGroup(): boolean {
      return !!useStoreSsPlayer().playerGroupIds.some((group) => {
        return group.startsWith(EPlayerWritableGroup.VIP_LEVEL_UP_NOTIFY);
      });
    },
    vipGroupLevel(): number {
      if (this.isVipGroup) {
        return useStoreSsPlayer().playerLevel;
      }

      return NaN;
    },
    isVipSeenGroup(): boolean {
      return !!useStoreSsPlayer().playerGroupIds.some((group) => {
        return group.startsWith(EPlayerWritableGroup.LEVEL_UP_MODAL_SHOWN_ID);
      });
    },
    vipSeenGroupLevel(): number {
      if (this.isVipSeenGroup) {
        const groups = useStoreSsPlayer().playerGroupIds.filter((group) => {
          return group.startsWith(EPlayerWritableGroup.LEVEL_UP_MODAL_SHOWN_ID);
        });

        const level = Math.max(
          ...groups.map((group) => {
            const groupSubParts = group?.split('-') || [];
            // parse int because id is set by 2 digits, like 'vip-level-02'
            return parseInt(groupSubParts[groupSubParts.length - 1]);
          })
        );

        if (level < this.vipGroupLevel) {
          return this.vipGroupLevel - 1;
        }
        return level;
      }

      return NaN;
    },
  },

  actions: {
    async loadVipData(force: boolean = false) {
      if (!force && this.data.length > 0) {
        return;
      }

      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'bonusType',
          'levelImage',
          'rewards',
          'rewards.icon',
          'avatar',
        ]);

        this.requestPromise = strapi()
          .get('vip-level-datas', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData(response.data);
        const filteredState = this.data.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.data = [...filteredState, ...parsedData].sort((a, b) => a.level - b.level);

        return true;
      } else {
        return false;
      }
    },
    openModalIfNeeded() {
      const hasCurrLevelGroup = !!useStoreSsPlayer().playerGroupIds.find((group) => {
        const isVipGroup = group.startsWith(EPlayerWritableGroup.VIP_LEVEL_ID);

        if (!isVipGroup) {
          return false;
        }

        const groupSubParts = group.split('-');
        // parse int because id is set by 2 digits, like 'vip-level-02'
        const vipLevel = parseInt(groupSubParts[groupSubParts.length - 1]);
        return vipLevel === useStoreSsPlayer().playerLevel;
      });

      if (!hasCurrLevelGroup) {
        this.useGroupRequest(0, true);
        return;
      }

      const hasCurrLevelShownGroup = !!useStoreSsPlayer().playerGroupIds.find((group) => {
        const isLevelupShownGroup = group.startsWith(EPlayerWritableGroup.LEVEL_UP_MODAL_SHOWN_ID);
        if (!isLevelupShownGroup) {
          return false;
        }

        const groupSubParts = group.split('-');
        // parse int because id is set by 2 digits, like 'vip-level-02'
        const vipLevel = parseInt(groupSubParts[groupSubParts.length - 1]);
        return vipLevel === useStoreSsPlayer().playerLevel;
      });

      if (!hasCurrLevelShownGroup) {
        this.isNotificationShown = true;
      }
    },
    useGroupRequest(requestNumber: number, skipCheckingOpenModalIfNeeded?: boolean) {
      if (this.isPolingGroups) {
        return;
      }

      this.isPolingGroups = true;

      useStoreSsPlayer().loadPlayerInfo();
      useStoreSsPlayer().loadUserCompPoints(true);

      const currLevelByGroup = useStoreSsPlayer().playerGroupIds.reduce((maxLevel, currGroup) => {
        const isVipGroup = currGroup.startsWith(EPlayerWritableGroup.VIP_LEVEL_ID);
        if (!isVipGroup) {
          return maxLevel;
        }

        const groupSubParts = currGroup.split('-');
        // parse int because id is set by 2 digits, like 'vip-level-02'
        const vipLevel = parseInt(groupSubParts[groupSubParts.length - 1]);

        return Math.max(maxLevel, vipLevel);
      }, 0);

      const isGroupLevelEqualToPoints = currLevelByGroup >= useStoreSsPlayer().playerLevel;
      const isExceededMaxRequestNumber = requestNumber >= MAX_USER_GROUP_REQUESTS;

      // No need to continue poling
      if (isGroupLevelEqualToPoints || isExceededMaxRequestNumber) {
        this.isPolingGroups = false;
        if (!skipCheckingOpenModalIfNeeded) {
          this.openModalIfNeeded();
        }
        return;
      }

      setTimeout(() => {
        this.useGroupRequest(requestNumber + 1);
      }, USER_GROUP_REQUEST_DELAY);
    },
    onLevelupClose() {
      const groupToAdd = `${EPlayerWritableGroup.LEVEL_UP_MODAL_SHOWN_ID}${
        useStoreSsPlayer().playerLevel
      }`;

      // user opened already closed levelup modal by link
      if (useStoreSsPlayer().playerGroupIds.includes(groupToAdd)) {
        return;
      }

      useStoreSsPlayer().updatePlayerGroups({ add: [groupToAdd] });
      this.isNotificationShown = false;
    },
    hideNotification() {
      this.isNotificationShown = false;
    },
  },
});
